import React from 'react';
import '../styles/PublicPages.css';
import { SchoolLegalHeader, useSchoolLegalBranding } from '../components/SchoolLegalBranding';

const TermsAndConditions = () => {
  const branding = useSchoolLegalBranding();
  return (
    <div
      className={branding.isSchool ? 'staff-page legal-page legal-page--school' : 'staff-page legal-page'}
      style={branding.style}
    >
      <div className="auth-container">
        <SchoolLegalHeader branding={branding} />

        <div className="auth-form">
          <div className="terms-content">
            <h1 className="terms-main-title">Terms and Conditions</h1>

            <div className="terms-intro">
              <p className="terms-text">
                Welcome to Homecrowd Inc ("Homecrowd," "we," "us," or "our"). These Terms & Conditions ("Terms") govern your use of the Homecrowd mobile application, website, and any related services (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms.
              </p>
              <p className="terms-text terms-highlight">
                If you do not agree, you must not use the Services.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">1. Eligibility</h2>
              <p className="terms-text">To use Homecrowd, you must:</p>
              <ul className="terms-list">
                <li>Be at least 13 years old (or the minimum age in your jurisdiction).</li>
                <li>Create an account with accurate information.</li>
                <li>Agree to comply with these Terms and all applicable laws.</li>
              </ul>
              <p className="terms-text">
                If you use the Services on behalf of an organization, you represent that you have the authority to bind that organization.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">2. Account Registration</h2>
              <p className="terms-text">
                You may need to create an account to use certain features of the Services. You agree to:
              </p>
              <ul className="terms-list">
                <li>Provide accurate and complete information.</li>
                <li>Keep your login credentials secure.</li>
                <li>Notify us immediately of any unauthorized use.</li>
              </ul>
              <p className="terms-text">
                You are responsible for all activity under your account. We reserve the right to refuse or suspend accounts at our discretion.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">3. Card Linking and Transaction Monitoring</h2>
              <p className="terms-text">
                Homecrowd partners with a secure third-party card-linking provider to enable users to link eligible payment cards and earn points from in-network transactions.
              </p>

              <div className="terms-subsection">
                <h3 className="terms-subsection-title">3.1 How Card Linking Works</h3>
                <ul className="terms-list">
                  <li>When you link a card, the card-linking provider securely tokenizes your card information.</li>
                  <li>Homecrowd does not receive or store your full card number, CVV, or expiration date.</li>
                  <li>We receive transaction metadata (not item-level details) for eligible in-network purchases.</li>
                  <li>This data is used solely to award points and manage rewards.</li>
                </ul>
              </div>

              <div className="terms-subsection">
                <h3 className="terms-subsection-title">3.2 Authorization</h3>
                <p className="terms-text">By linking a card, you authorize:</p>
                <ul className="terms-list">
                  <li>The card-linking provider to access your transaction data</li>
                  <li>The sharing of qualifying transaction metadata with Homecrowd</li>
                  <li>Homecrowd to calculate points based on this data</li>
                </ul>
                <p className="terms-text">You may disconnect your linked card at any time.</p>
              </div>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">4. Rewards and Points Program</h2>
              <p className="terms-text">
                Homecrowd allows users to earn points for qualifying in-network transactions and redeem them for rewards.
              </p>

              <div className="terms-subsection">
                <h3 className="terms-subsection-title">4.1 Earning Points</h3>
                <p className="terms-text">
                  Points are earned based on program rules, participating merchants, and eligibility criteria. We may modify:
                </p>
                <ul className="terms-list">
                  <li>Point earning rates</li>
                  <li>Eligible merchants</li>
                  <li>Bonus programs</li>
                  <li>Promotional offers</li>
                </ul>
              </div>

              <div className="terms-subsection">
                <h3 className="terms-subsection-title">4.2 Redeeming Points</h3>
                <p className="terms-text">
                  Points may be redeemed for rewards available within the app. Points:
                </p>
                <ul className="terms-list">
                  <li>Have no cash value</li>
                  <li>Are non-transferable</li>
                  <li>Cannot be exchanged outside the platform</li>
                  <li>May expire or be changed according to program rules</li>
                </ul>
              </div>

              <div className="terms-subsection">
                <h3 className="terms-subsection-title">4.3 Program Changes</h3>
                <p className="terms-text">
                  We may alter or discontinue the rewards program at any time. We will provide notice if changes materially affect your accumulated points.
                </p>
              </div>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">5. Prohibited Conduct</h2>
              <p className="terms-text">You agree not to:</p>
              <ul className="terms-list">
                <li>Use the Services for any unlawful purpose</li>
                <li>Attempt to manipulate or fraudulently earn points</li>
                <li>Access or attempt to access accounts without permission</li>
                <li>Bypass, disable, or interfere with security features</li>
                <li>Reverse engineer or modify the Services</li>
                <li>Use automated tools (bots, scrapers) without permission</li>
              </ul>
              <p className="terms-text">
                We may suspend or terminate accounts engaging in prohibited activities.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">6. Intellectual Property</h2>
              <p className="terms-text">
                All content provided by Homecrowd—including text, graphics, logos, icons, software, and design—is owned by Homecrowd or its licensors and protected by intellectual property laws.
              </p>
              <p className="terms-text">You may not:</p>
              <ul className="terms-list">
                <li>Copy</li>
                <li>Modify</li>
                <li>Distribute</li>
                <li>Sell</li>
                <li>Reproduce</li>
                <li>Create derivative works</li>
              </ul>
              <p className="terms-text">without our explicit permission.</p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">7. Third-Party Services</h2>
              <p className="terms-text">
                Our Services may include integrations with third-party providers (e.g., card-linking provider, cloud services). We are not responsible for:
              </p>
              <ul className="terms-list">
                <li>Their actions</li>
                <li>Their privacy practices</li>
                <li>The accuracy of information they provide</li>
                <li>Content, products, or services from those providers</li>
              </ul>
              <p className="terms-text">Your use of any third-party service is at your own risk.</p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">8. Disclaimer of Warranties</h2>
              <p className="terms-text">
                You agree that use of the Services is at your own risk. The Services are provided "as is" and "as available", without warranties of any kind, including:
              </p>
              <ul className="terms-list">
                <li>Fitness for a particular purpose</li>
                <li>Accuracy</li>
                <li>Reliability</li>
                <li>Security</li>
                <li>Non-infringement</li>
                <li>Availability or uptime guarantees</li>
              </ul>
              <p className="terms-text">We do not guarantee that:</p>
              <ul className="terms-list">
                <li>The Services will be uninterrupted</li>
                <li>The Services will be error-free</li>
                <li>Rewards or points will always be available</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">9. Limitation of Liability</h2>
              <p className="terms-text">
                To the maximum extent permitted by law, Homecrowd and its affiliates shall not be liable for:
              </p>
              <ul className="terms-list">
                <li>Loss of profits</li>
                <li>Loss of data</li>
                <li>Loss of rewards or points</li>
                <li>Indirect, incidental, or consequential damages</li>
                <li>Unauthorized access to data</li>
                <li>Errors or omissions in the Services</li>
                <li>Any damages arising from your use of or inability to use the Services</li>
              </ul>
              <p className="terms-text">Our total liability to you will not exceed the greater of:</p>
              <ul className="terms-list">
                <li>(1) The amount you paid us in the last 12 months (typically $0) OR</li>
                <li>(2) $100</li>
              </ul>
              <p className="terms-text">
                Some jurisdictions do not allow limitations; in those cases, only the legal minimum applies.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">10. Indemnification</h2>
              <p className="terms-text">
                You agree to defend, indemnify, and hold harmless Homecrowd, its officers, employees, and partners from any claims arising out of:
              </p>
              <ul className="terms-list">
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Fraudulent or improper account activity</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">11. Termination</h2>
              <p className="terms-text">We may suspend or terminate your account if:</p>
              <ul className="terms-list">
                <li>You violate these Terms</li>
                <li>You engage in fraud or abuse</li>
                <li>You misuse the Services</li>
                <li>We discontinue the Services</li>
              </ul>
              <p className="terms-text">
                You may delete your account at any time. Sections regarding intellectual property, disclaimers, limitations, and indemnification will survive termination.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">12. Changes to These Terms</h2>
              <p className="terms-text">
                We may update these Terms at any time. If changes are material, we will provide notice (via email or in-app message). Continued use of the Services after changes take effect constitutes acceptance of the revised Terms.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">13. Governing Law</h2>
              <p className="terms-text">
                These Terms are governed by the laws of the State of [Insert State — likely North Carolina or your state of incorporation], without regard to conflict-of-law principles. Any disputes must be resolved exclusively in courts located in [Insert County & State], unless otherwise required by applicable law.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">14. Contact Us</h2>
              <p className="terms-text">
                If you have questions about these Terms or the Services, contact us at:
              </p>
              <div className="contact-info">
                <p className="terms-text"><strong>Homecrowd Inc</strong></p>
                <p className="terms-text">Email: <a href="mailto:support@gethomecrowd.com" className="terms-link">support@gethomecrowd.com</a></p>
                <p className="terms-text"><strong>Last updated: January 2026</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;